import { z } from 'zod';

/**
 * Recruitment Form Validation Schema
 * All validation rules for loan officer recruitment form
 */

// Phone number validation (10+ digits, flexible formatting)
const phoneRegex = /^[\d\s\-().]+$/;

// NMLS number validation (5-10 digits)
const nmlsRegex = /^\d{5,10}$/;

// DRE number validation (8 digits, optional DRE# prefix)
const dreRegex = /^(DRE#?)?\d{8}$/i;

/**
 * Normalize an optional numeric form input BEFORE any coercion.
 *
 * `z.coerce.number()` must never see `''`: `Number('')` is `0`, which satisfies
 * `.min(0)`, so a blank field silently parses to 0 and a trailing
 * `.or(z.literal(''))` becomes unreachable dead code. That wrote 0 into
 * years-of-experience on unrelated saves and let a blank mentor split slip past
 * the "required when a mentor is selected" refinement.
 *
 * Empty -> undefined (absent). A non-numeric string is returned untouched so
 * the numeric schema rejects it rather than silently treating it as blank.
 */
const optionalNumericInput = (val: unknown): unknown => {
  if (val === '' || val === null || val === undefined) {
    return undefined;
  }
  if (typeof val === 'string') {
    const trimmed = val.trim();
    if (trimmed === '') {
      return undefined;
    }
    const parsed = Number(trimmed);
    return Number.isNaN(parsed) ? val : parsed;
  }
  return val;
};

export const recruitmentFormSchema = z.object({
  // Personal Information
  firstName: z.string()
    .min(1, 'Legal first name is required')
    .max(100, 'Legal first name must be less than 100 characters')
    .trim(),

  lastName: z.string()
    .min(1, 'Legal last name is required')
    .max(100, 'Legal last name must be less than 100 characters')
    .trim(),

  preferredFirstName: z.string()
    .max(100, 'Preferred first name must be less than 100 characters')
    .optional()
    .or(z.literal(''))
    .transform(val => val?.trim() || ''),

  email: z.string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address')
    .max(255, 'Email must be less than 255 characters')
    .toLowerCase()
    .trim(),

  phone: z.string()
    .min(10, 'Phone number must be at least 10 digits')
    .max(20, 'Phone number is too long')
    .regex(phoneRegex, 'Please enter a valid phone number')
    .trim(),

  dateOfBirth: z.string()
    .optional()
    .or(z.literal('')),

  // Mailing Address
  mailingStreetAddress: z.string()
    .max(200, 'Street address must be less than 200 characters')
    .optional()
    .or(z.literal(''))
    .transform(val => val?.trim() || ''),

  mailingCity: z.string()
    .max(100, 'City must be less than 100 characters')
    .optional()
    .or(z.literal(''))
    .transform(val => val?.trim() || ''),

  mailingState: z.string()
    .max(2, 'State must be 2 characters')
    .optional()
    .or(z.literal(''))
    .transform(val => val?.trim().toUpperCase() || ''),

  mailingZip: z.string()
    .max(10, 'ZIP code must be less than 10 characters')
    .optional()
    .or(z.literal(''))
    .transform(val => val?.trim() || ''),

  // Professional Information
  nmlsNumber: z.string()
    .min(1, 'NMLS number is required')
    .regex(nmlsRegex, 'NMLS number must be 5-10 digits')
    .trim(),

  dreNumber: z.string()
    .optional()
    .or(z.literal(''))
    .refine(
      (val) => !val || dreRegex.test(val),
      'DRE number must be 8 digits (e.g., 01234567 or DRE#01234567)'
    ),

  // Blank must land as null (the database column is nullable), never 0.
  yearsOfExperience: z.preprocess(
    optionalNumericInput,
    z.number({ invalid_type_error: 'Please enter a valid number' })
      .min(0, 'Years of experience cannot be negative')
      .max(50, 'Years of experience seems too high')
      .optional()
  ).transform(val => (val === undefined ? null : val)),

  position: z.enum(['Loan Officer', 'Senior Loan Officer', 'Branch Manager'], {
    required_error: 'Position is required',
    invalid_type_error: 'Please select a valid position'
  }),

  stateLicenses: z.array(z.string())
    .min(1, 'At least one state license is required')
    .max(50, 'Too many states selected'),

  bankedCompensation: z.preprocess(
    (val) => {
      // Handle empty values
      if (val === '' || val === null || val === undefined) {
return undefined;
}
      // Handle numeric strings and numbers
      const num = typeof val === 'string' ? parseFloat(val) : val;
      return isNaN(num as number) ? undefined : num;
    },
    z.number()
      .min(0.75, 'Banked compensation must be at least 0.75%')
      .max(3, 'Banked compensation cannot exceed 3%')
      .optional()
  ),

  brokerCommissionSplit: z.preprocess(
    (val) => {
      // Handle empty values - return undefined for required field check
      if (val === '' || val === null || val === undefined) {
return undefined;
}
      // Handle numeric strings and numbers
      const num = typeof val === 'string' ? parseFloat(val) : val;
      return isNaN(num as number) ? undefined : num;
    },
    z.number({
      required_error: 'Broker commission split is required',
      invalid_type_error: 'Please enter a valid commission split percentage'
    })
      .min(0, 'Commission split cannot be negative')
      .max(85, 'Commission split cannot exceed 85%')
  ),

  // Blank must land as undefined, never 0 — 0 bps is a legitimate stored value.
  bankedOverrideBps: z.preprocess(
    optionalNumericInput,
    z.number({ invalid_type_error: 'Please enter a valid number' })
      .min(0, 'Override cannot be negative')
      .max(50, 'Override cannot exceed 50 basis points')
      .optional()
  ),

  overrideRecipientId: z.string()
    .uuid('Invalid user ID')
    .optional()
    .or(z.literal('')),

  // Blank must land as undefined so the mentor-split refinement below can see
  // it. Coercing blank to 0 let a selected mentor bypass that required check.
  brokerMentorCommissionSplit: z.preprocess(
    optionalNumericInput,
    z.number({ invalid_type_error: 'Please enter a valid percentage' })
      .min(0, 'Mentor split cannot be negative')
      .max(50, 'Mentor split cannot exceed 50%')
      .optional()
  ),

  mentorId: z.string()
    .uuid('Invalid user ID')
    .optional()
    .or(z.literal('')),

  // Maximum Compensation (per-deal cap)
  maximumCompensation: z.preprocess(
    (val) => {
      if (val === '' || val === null || val === undefined) {
return undefined;
}
      // Strip commas from formatted strings like "75,000"
      const cleaned = typeof val === 'string' ? val.replace(/,/g, '') : val;
      const num = typeof cleaned === 'string' ? parseFloat(cleaned) : cleaned;
      return isNaN(num as number) ? undefined : num;
    },
    z.number()
      .min(10000, 'Maximum compensation must be at least $10,000')
      .max(999999, 'Maximum compensation cannot exceed $999,999')
      .optional()
  ),

  // Status (must match RecruitStatus from database-recruitment.ts)
  status: z.enum(['Inquiry', 'Pending', 'In Progress', 'Approved', 'Denied', 'On Hold', 'Terminated'], {
    required_error: 'Status is required'
  }),

  statusComment: z.string()
    .max(500, 'Comment must be less than 500 characters')
    .optional()
    .or(z.literal('')),

  // Recruiter Assignment
  recruiterId: z.string()
    .min(1, 'Recruiter assignment is required')
    .uuid('Invalid recruiter ID')
}).superRefine((data, ctx) => {
  // If mentor is selected, mentor split is required
  if (data.mentorId && data.mentorId !== '') {
    if (data.brokerMentorCommissionSplit === undefined ||
        data.brokerMentorCommissionSplit === null ||
        (data.brokerMentorCommissionSplit as unknown) === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Mentor split is required when a mentor is selected',
        path: ['brokerMentorCommissionSplit']
      });
    }
  }
});

export type RecruitmentFormValues = z.infer<typeof recruitmentFormSchema>;

/**
 * Helper validation functions
 */

export const validateNMLSNumber = (value: string): boolean => {
  return nmlsRegex.test(value);
};

export const validateDRENumber = (value: string): boolean => {
  if (!value) {
return true;
} // Optional field
  return dreRegex.test(value);
};

export const formatPhoneNumber = (value: string): string => {
  // Remove all non-digits
  const cleaned = value.replace(/\D/g, '');

  // Format as (XXX) XXX-XXXX if 10 digits
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }

  // Format as +X (XXX) XXX-XXXX if 11 digits (includes country code)
  if (cleaned.length === 11) {
    return `+${cleaned.slice(0, 1)} (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }

  return value;
};

export const formatDRENumber = (value: string): string => {
  // Remove DRE# prefix if present
  const cleaned = value.replace(/^DRE#?/i, '');

  // Add DRE# prefix if 8 digits
  if (/^\d{8}$/.test(cleaned)) {
    return `DRE#${cleaned}`;
  }

  return value;
};
